class SinglePostToc {
	private links: HTMLAnchorElement[];

	private headings: HTMLElement[];

	private indicator: HTMLElement | null;

	private ticking = false;

	constructor(private root: HTMLElement) {
		this.links = Array.from(root.querySelectorAll<HTMLAnchorElement>('[data-single-post-toc-link]'));
		this.indicator = root.querySelector<HTMLElement>('[data-single-post-toc-indicator]');
		this.headings = this.links
			.map((link) => {
				const id = decodeURIComponent(link.hash.replace('#', ''));
				return id ? document.getElementById(id) : null;
			})
			.filter((heading): heading is HTMLElement => Boolean(heading));

		if (!this.links.length || !this.headings.length) {
			return;
		}

		this.bindClicks();
		this.observeHeadings();
		this.bindScrollFallback();
		this.setActive(this.links[0]);
	}

	private bindClicks(): void {
		this.links.forEach((link) => {
			link.addEventListener('click', () => {
				this.setActive(link);
				window.setTimeout(() => this.updateFromScroll(), 350);
			});
		});
	}

	private observeHeadings(): void {
		const observer = new IntersectionObserver(
			(entries) => {
				const visibleEntry = entries
					.filter((entry) => entry.isIntersecting)
					.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

				if (!visibleEntry) {
					return;
				}

				const activeLink = this.links.find((link) => link.hash === `#${visibleEntry.target.id}`);

				if (activeLink) {
					this.setActive(activeLink);
				}
			},
			{
				rootMargin: '-20% 0px -65% 0px',
				threshold: 0,
			}
		);

		this.headings.forEach((heading) => observer.observe(heading));
	}

	private bindScrollFallback(): void {
		window.addEventListener(
			'scroll',
			() => {
				if (this.ticking) {
					return;
				}

				this.ticking = true;
				window.requestAnimationFrame(() => {
					this.updateFromScroll();
					this.ticking = false;
				});
			},
			{passive: true}
		);

		window.addEventListener('resize', () => this.updateFromScroll());
	}

	private updateFromScroll(): void {
		const activationOffset = Math.min(window.innerHeight * 0.35, 220);
		const passedHeadings = this.headings.filter(
			(heading) => heading.getBoundingClientRect().top <= activationOffset
		);
		const activeHeading = passedHeadings[passedHeadings.length - 1] ?? this.headings[0];

		const activeLink = this.links.find((link) => {
			const id = decodeURIComponent(link.hash.replace('#', ''));
			return id === activeHeading.id;
		});

		if (activeLink) {
			this.setActive(activeLink);
		}
	}

	private setActive(activeLink: HTMLAnchorElement): void {
		this.links.forEach((link) => {
			link.classList.toggle('-is-active', link === activeLink);
		});

		this.moveIndicator(activeLink);
	}

	private moveIndicator(activeLink: HTMLAnchorElement): void {
		if (!this.indicator) {
			return;
		}

		this.indicator.style.top = `${activeLink.offsetTop}px`;
		this.indicator.style.height = `${activeLink.offsetHeight}px`;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll<HTMLElement>('[data-single-post]').forEach((block) => {
		new SinglePostToc(block);
	});
});
